import React, { createContext, useContext } from "react";

import { arrayOrEmpty } from "utils";

import { AutomationCommand } from "../types/automation";
import { ChildCommands } from "../types/payloads";
import { CommandResult, CommandResultPayload } from "../types/results";
import { Topic } from "../types/topics";

export interface AutomationHandler {
  id: string;
  handles: (topic: Topic) => boolean;
  executeCommand: (command: AutomationCommand) => Promise<CommandResult>;
}

export class AutomationBroker {
  handlers: AutomationHandler[] = [];

  registerHandler(handler: AutomationHandler) {
    const handlerIndex = this.handlers.findIndex((h) => h.id === handler.id);
    if (handlerIndex >= 0) {
      this.handlers.splice(handlerIndex, 1, handler);
    } else {
      this.handlers.push(handler);
    }
  }

  async blobToString(blob: Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Resolve the promise with the base64 string
        resolve(reader.result.toString().split(",")[1]);
      };
      reader.onerror = () => {
        reject(new Error("Error reading blob as base64"));
      };
      reader.readAsDataURL(blob);
    });
  }
  async executeCommands(commands: AutomationCommand[]): Promise<CommandResultPayload[]> {
    const results: CommandResultPayload[] = [];

    for (const command of commands) {
      for (const handler of this.handlers) {
        if (!handler?.handles(command.topic)) continue;
        const childCommands = (command.payload as ChildCommands)?.commands;
        if (arrayOrEmpty(childCommands).length > 0) {
          for (const childCmd of childCommands) {
            const result = await handler.executeCommand({
              ...childCmd,
              payload: command.payload
            });
            if (result?.status === "error") return results;
            if (result?.payload) results.push(result.payload);
          }
        }

        const result = await handler.executeCommand(command);
        if (result?.status === "error") return results;
        if (result?.payload) results.push(result.payload);
      }
    }

    return results;
  }
}

// Create a Context type that includes the dispatcher function
interface AutomationBrokerContextType {
  broker: AutomationBroker;
}

const broker = new AutomationBroker();
const AutomationBrokerContext = createContext<AutomationBrokerContextType>({
  broker
});

// React Hook to use Broker
export function useRegisterHandlerWithBroker(handler: AutomationHandler) {
  const context = useContext(AutomationBrokerContext);
  context.broker.registerHandler(handler);
  return context;
}

export function useAutomationBroker(): AutomationBroker {
  const context = useContext(AutomationBrokerContext);
  return context.broker;
}

// A wrapper component
export const AutomationBrokerProvider: React.FC = ({ children }) => {
  return (
    <AutomationBrokerContext.Provider value={{ broker }}>
      {children}
    </AutomationBrokerContext.Provider>
  );
};
